import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDialogDismiss } from '../../components/ui/useDialogDismiss';
import { fetchQuotationLineCatalogueRow, type QuotationLineCatalogueRowDto } from '../../lib/quotationApi';
import { shownByUnit } from '../catalogue/costPriceTiers';
import { describePackaging, conversionsForItem } from '../../utils/unitConversions';
import { isSameUnit } from '../../utils/unitVocabulary';
import { isPseudoVendorName } from '../../utils/poSendStatus';
import { formatSourceLabel, priceSourceLabel } from './sourceLabel';
import { formatCurrency } from '../../utils/priceUtils';
import { formatDate } from '../../utils/dateFormat';

/**
 * The read-only pricing details a quotation line's Supplier cell expands into
 * (owner decisions 2026-09-24) — replaces what used to be a click into the
 * merged `ItemLookupPanel`, which JP found confusing: "West code x3" read as
 * three suppliers for THIS item, when the count was really how many rows the
 * lookup would open with across every match on the SEARCH, not on the line's
 * own product. This card states only facts about the one row the line is
 * actually priced from — nothing to compare, nothing to pick.
 *
 * Anchored to the Supplier cell's own button (`anchorRef`), positioned by
 * measuring it once on open — the same idea `ModuleInfoButton.place()` uses,
 * simplified: this card always opens directly under a table cell, never near
 * a screen edge tight enough to need ModuleInfoButton's left/right flip. It
 * portals to `document.body` and uses `position: fixed` for the same reason
 * ModuleInfoButton does: the quotation grid's own row container scrolls
 * (`overflow-x-auto overflow-y-auto`), which would both clip an absolutely
 * positioned child and carry it away from its anchor on scroll.
 *
 * Dismiss reuses `useDialogDismiss` — Escape (only when this is the topmost
 * dialog) and a click that both starts and ends on the invisible backdrop —
 * the same mechanism `Modal`/`ActionDialog` use, just with a transparent
 * backdrop instead of a dimming one: this is a lightweight info popover, not
 * a modal task.
 *
 * The money is read, never re-derived: `priceLevels.byUnit` is written once,
 * server-side, by `costEngine.computeGraphPriceLevels` (see
 * `CatalogueItemForm`'s own "Priced at, as last saved" block, which this
 * mirrors). A row whose `byUnit` does not (yet) cover every unit its
 * conversion graph reaches — e.g. it prices CASE and KG but not LB even
 * though the graph connects LB too — is shown exactly as stored, gap and
 * all; computing the missing tier here would be a second price engine.
 */

const CARD_WIDTH = 320;
const GAP = 4;
const EDGE = 8;

export interface SupplierDetailsCardLine {
  /** How this line was priced, when no catalogue row backs it — `formatSourceLabel` reads the same field the fallback line names it by. */
  pricingSource?: string | null;
  /** The vendor already on the line — shown only in the no-catalogue-row fallback; the row's own `supplier` is shown otherwise. Pseudo-vendors hidden via `isPseudoVendorName`, same as the cell's own display. */
  selectedVendor?: string | null;
  /** The unit the line is CURRENTLY quoted in — marked in the row's per-unit price list. */
  uom?: string | null;
  /**
   * This line's own stated TT$ unit cost — `utils/pricing.unitCostTtd(item, ttdToUsdRate)`,
   * resolved by the caller. Never recomputed here: invariant 8 / the pricing
   * owner's own doc comment says the pinned TT$ is read back, not re-derived.
   */
  unitCostTtd?: number | null;
  /**
   * D86/D86: the date the line's own source price counts from, as the line STORED it:
   * `storedPriceDate` reads `pricingDetails.sourcePriceDate` first, then
   * `priceChoice.chosen.date`. Read only in the no-catalogue-row fallback.
   * Missing -> "No date"; never invented.
   */
  priceDate?: string | null;
}

export interface SupplierDetailsCardProps {
  isOpen: boolean;
  onClose: () => void;
  /** The Supplier cell's own toggle button — read once on open to place the card. */
  anchorRef: React.RefObject<HTMLElement>;
  /**
   * `pricingDetails.masterCatalogueId` off the line's DTO (check the DTO).
   * Null/undefined skips the fetch and goes straight to the fallback line —
   * a client-side SHORTCUT only (the server independently re-resolves the
   * id off the line itself; see `quotationId`/`itemIndex` below), so a line
   * this card already knows carries no catalogue id never fires a request
   * that can only 404.
   */
  masterCatalogueId?: string | null;
  /**
   * N7 (owner decision, 2026-09-25): the quotation this line belongs to, and
   * the line's own POSITION in `items` — what the new
   * `GET /quotations/:id/items/:itemIndex/catalogue-row` endpoint addresses a
   * line by (the endpoint resolves `masterCatalogueId` off the line
   * SERVER-SIDE; `masterCatalogueId` above is read here only to skip a
   * request that would just 404). This endpoint is gated on QUOTATIONS view
   * alone — unlike the general `GET /master-catalogue/:id` this card used to
   * call, which needs PRODUCTS view and so silently showed nothing to a
   * person who could open this quotation but not the Master Catalogue.
   */
  quotationId: string;
  itemIndex: number;
  line: SupplierDetailsCardLine;
}

// The owner's label and grouping (BUSINESS_RULES#quotation-screen-money-labels),
// rounded to the cent rather than ceiled — this card shows the TT$ the line was
// priced at, exactly.
function fmtTtd(n: number): string {
  return formatCurrency(n, 'TTD', { ceil: false });
}

/**
 * The date a line STORED for its source price: the recorded supplier choice's
 * `chosen.date` (or, on a D23-era line, its `invoiceDate`). Null when the line
 * recorded none - the card then says "No date"; nothing is looked up or invented.
 */
export function storedPriceDate(pricingDetails: unknown): string | null {
  const details = pricingDetails as any;
  // A supplier-row pick stores the option's date directly; a generator catalogue
  // pick records it on the supplier choice.
  const chosen = details?.priceChoice?.chosen;
  const date = details?.sourcePriceDate ?? chosen?.date ?? chosen?.invoiceDate;
  return typeof date === 'string' && date.trim() ? date : null;
}

export const SupplierDetailsCard: React.FC<SupplierDetailsCardProps> = ({
  isOpen,
  onClose,
  anchorRef,
  masterCatalogueId,
  quotationId,
  itemIndex,
  line,
}) => {
  const [row, setRow] = useState<QuotationLineCatalogueRowDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rowGone, setRowGone] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const dismiss = useDialogDismiss(isOpen, onClose);

  useEffect(() => {
    if (!isOpen) return;
    const anchor = anchorRef.current;
    if (anchor) {
      const r = anchor.getBoundingClientRect();
      const left = Math.max(EDGE, Math.min(r.left, window.innerWidth - CARD_WIDTH - EDGE));
      setPos({ top: r.bottom + GAP, left });
    }

    setRow(null);
    setError(null);
    setRowGone(false);

    const id = typeof masterCatalogueId === 'string' ? masterCatalogueId.trim() : '';
    if (!id) return;

    let cancelled = false;
    setLoading(true);
    // N7 (owner decision, 2026-09-25): the quotation-scoped read, gated on
    // QUOTATIONS view alone — the id is resolved off THIS line's own
    // `pricingDetails.masterCatalogueId` server-side; `id` above is read only
    // to skip firing a request a line with no catalogue id could only 404.
    fetchQuotationLineCatalogueRow(quotationId, itemIndex)
      .then((res) => {
        if (cancelled) return;
        if (res?.success && res.data) setRow(res.data);
        // `success: false` with a 2xx is not a real answer this card has seen
        // elsewhere; treat it the same as a thrown error rather than guessing.
        else setError('The Master Catalogue did not return this row.');
      })
      .catch((err: any) => {
        if (cancelled) return;
        // A 404 means this line carries no catalogue row right now —
        // QUOTATION_LINE_NO_CATALOGUE_ROW (never priced from one, or the row
        // it priced from is gone since) — genuinely "no catalogue row",
        // never a failure to report.
        if (err?.status === 404) {
          setRowGone(true);
          return;
        }
        // Belt-and-braces: this endpoint is gated on QUOTATIONS view ALONE
        // (N7 closes the exact gap the previous comment here described —
        // PRODUCTS view is no longer required at all), so a 403 should not be
        // reachable by anyone who could open this quotation to click the
        // cell. Kept anyway rather than falling through to the generic
        // sentence below, in case a future gate change reintroduces it.
        if (err?.status === 403) {
          setError('You do not have access to catalogue details.');
          return;
        }
        // Never the server's own prose (invariant 6) — a network failure or
        // any other refusal reads the same fixed sentence, the same
        // convention `confirmOrderErrorMessage` / `supplierPickErrorMessage`
        // use elsewhere on this page.
        setError("Could not load this item's catalogue row. Please try again.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, masterCatalogueId, quotationId, itemIndex, anchorRef]);

  if (!isOpen || !pos) return null;

  const noRow = !row && (rowGone || !masterCatalogueId);
  const sourceLine = formatSourceLabel(line.pricingSource || undefined).label;
  const lineVendor = !isPseudoVendorName(line.selectedVendor) ? line.selectedVendor : null;

  let body: React.ReactNode;
  if (loading) {
    body = (
      <div className="flex items-center justify-center py-4 text-slate-500" aria-label="Loading catalogue row">
        <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#D7DBD9] border-t-primary-500" />
        <span className="ml-2">Loading…</span>
      </div>
    );
  } else if (error) {
    body = <div className="text-red-600">{error}</div>;
  } else if (row) {
    const described = describePackaging(conversionsForItem(row), row.unit);
    const packagingIsGap = !described && !!row.packagingGap;
    const packaging = described || row.packagingGap || '—';
    const rowSupplier = !isPseudoVendorName(row.supplier) ? row.supplier : null;
    const rowSourceLabel = priceSourceLabel(row).label;
    const storedByUnit = shownByUnit(row).filter((e) => e && e.unit && e.ttAmount > 0);

    body = (
      <div className="space-y-2">
        <div>
          <p className="font-semibold text-slate-900 break-words">{row.item_name}</p>
        </div>
        <dl className="grid grid-cols-[auto,1fr] gap-x-2 gap-y-1 text-slate-600">
          <dt>Packaging</dt>
          <dd className={packagingIsGap ? 'text-[#888]' : 'text-slate-900'}>{packaging}</dd>
          <dt>Supplier</dt>
          <dd className="text-slate-900">{rowSupplier || '—'}</dd>
          <dt>Source</dt>
          <dd className="text-slate-900">{rowSourceLabel}</dd>
          <dt>Price date</dt>
          <dd className="text-slate-900">{formatDate(row.priceDate, 'No date')}</dd>
        </dl>
        {storedByUnit.length > 0 ? (
          <div className="pt-2 border-t border-dashed border-[#D7DBD9]">
            <p className="text-slate-500">Price, per unit:</p>
            <ul className="mt-1 space-y-0.5">
              {storedByUnit.map((e) => {
                const current = isSameUnit(e.unit, line.uom);
                return (
                  <li
                    key={e.unit}
                    className={`flex items-center justify-between ${current ? 'font-semibold text-primary-700' : 'text-slate-900'}`}
                  >
                    <span>
                      {e.unit}
                      {current && (
                        <span className="ml-1 text-xs font-semibold uppercase tracking-wide text-primary-600">
                          quoted at
                        </span>
                      )}
                    </span>
                    <span className="tabular-nums">{fmtTtd(e.ttAmount)}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : (
          <p className="pt-2 border-t border-dashed border-[#D7DBD9] text-slate-500">
            No per-unit prices saved for this row yet.
          </p>
        )}
      </div>
    );
  } else if (noRow) {
    body = (
      <div className="space-y-2">
        {/* R5 (owner decisions 2026-09-25): only the line's own TT$ figure is
            shown below, and that is acceptable — this line has no catalogue
            row to read a per-unit price list from, so there is nothing else
            here to show. The sentence says so, rather than reading like an
            unexplained gap. */}
        <p className="text-slate-900">
          Priced from {sourceLine || 'an unrecorded source'}. This line has no Master Catalogue row to
          read per-unit prices from — only its own cost is shown below.
        </p>
        <dl className="grid grid-cols-[auto,1fr] gap-x-2 gap-y-1 text-slate-600">
          <dt>Supplier</dt>
          <dd className="text-slate-900">{lineVendor || '—'}</dd>
          <dt>Unit</dt>
          <dd className="text-slate-900">{line.uom || '—'}</dd>
          <dt>Price date</dt>
          <dd className="text-slate-900">{formatDate(line.priceDate, 'No date')}</dd>
          <dt>TT$ unit cost</dt>
          <dd className="text-slate-900 tabular-nums">
            {line.unitCostTtd != null ? fmtTtd(line.unitCostTtd) : '—'}
          </dd>
        </dl>
      </div>
    );
  } else {
    // Should not happen (loading covers the fetch-in-flight window), but a
    // blank popover is a worse failure mode than a neutral line.
    body = <div className="text-slate-500">Nothing to show yet.</div>;
  }

  return createPortal(
    <>
      <div {...dismiss} className="fixed inset-0 z-[70] bg-transparent" aria-hidden="true" />
      <div
        role="dialog"
        aria-label="Pricing details"
        className="fixed z-[70] bg-white border border-[#D7DBD9] rounded-lg shadow-xl p-3 text-xs font-['Public_Sans'] leading-[16px] overflow-y-auto"
        style={{ top: pos.top, left: pos.left, width: CARD_WIDTH, maxHeight: 360 }}
      >
        {body}
      </div>
    </>,
    document.body
  );
};

export default SupplierDetailsCard;
